import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ElementType, ReactNode, useId } from "react";
import { useColorMode } from "src/design/ColorModeProvider";
import {
  heroBandOpacity,
  heroDots,
  heroLine,
  heroPanel,
  radius,
} from "src/design/tokens";
import { fontStretch } from "src/design/typography";

export interface HeroDecorationProps {
  /** Width of the diagonal band block, as a CSS length or percentage of the surface. */
  bandWidth?: string;
  /** Height of the band block; defaults to the full height. */
  bandHeight?: string;
  /** `clip-path` polygon of the band block. */
  clip?: string;
  /** Band period in px (design: 26 on bands, 30 on the sign-in panel). */
  period?: number;
  /** Dot grid size in px (design: 22 on bands, 18 on small cards, 26 on sign-in). */
  dotSize?: number;
  /** Omit the band block (small cards only draw the dots). */
  bands?: boolean;
}

/**
 * The design's hero decoration: a diagonal red/white band block on the right and a dot grid
 * over the whole surface. Absolutely positioned; the parent must be `position: relative` with
 * `overflow: hidden`.
 */
export const HeroDecoration = ({
  bandWidth = "38%",
  bandHeight = "100%",
  clip = "polygon(18% 0, 100% 0, 100% 100%, 0 100%)",
  period = 26,
  dotSize = 22,
  bands = true,
}: HeroDecorationProps) => {
  const theme = useTheme();
  const { mode } = useColorMode();
  const a = theme.palette.tokens.danger;
  const b =
    mode === "dark"
      ? theme.palette.tokens.primarySoft
      : theme.palette.tokens.surface;
  return (
    <>
      {bands ? (
        <Box
          aria-hidden
          data-testid="hero-bands"
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: bandWidth,
            height: bandHeight,
            background: `repeating-linear-gradient(-28deg, ${a} 0 ${period}px, ${b} ${period}px ${period * 2}px)`,
            opacity: heroBandOpacity[mode],
            clipPath: clip,
            pointerEvents: "none",
          }}
        />
      ) : null}
      <Box
        aria-hidden
        data-testid="hero-dots"
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `radial-gradient(${heroDots[mode]} 1.4px, transparent 1.6px)`,
          backgroundSize: `${dotSize}px ${dotSize}px`,
          pointerEvents: "none",
        }}
      />
    </>
  );
};

export interface HeroBandProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  /** Right-hand column (the design's join-code panel and action). */
  aside?: ReactNode;
  /** Heading element for the title; use "h2" below a PageHeader. Defaults to "h1". */
  component?: ElementType;
  /** Title size in px; the Today band uses 40, the session band 44. */
  titleSize?: number;
}

/**
 * Hero surface for the Today and session pages: 22 px radius, band and dot decoration,
 * eyebrow row, uppercase display title, body and an optional aside column.
 */
export const HeroBand = ({
  eyebrow,
  title,
  children,
  aside,
  component = "h1",
  titleSize = 40,
}: HeroBandProps) => {
  const titleId = useId();
  const theme = useTheme();
  const { mode } = useColorMode();
  const glow =
    mode === "dark"
      ? `radial-gradient(900px 320px at 18% 0%, ${alpha(theme.palette.primary.main, 0.35)}, transparent 60%)`
      : "none";
  return (
    <Box
      component="section"
      aria-labelledby={titleId}
      sx={{
        position: "relative",
        overflow: "hidden",
        bgcolor: "hero",
        backgroundImage: glow,
        color: "heroInk",
        borderRadius: `${radius.hero}px`,
        boxShadow: theme.shadows[1],
      }}
    >
      <HeroDecoration />
      <Box
        sx={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: aside
            ? { xs: "minmax(0, 1fr)", md: "minmax(0, 1.3fr) minmax(0, 1fr)" }
            : "minmax(0, 1fr)",
          gap: 3.5,
          p: { xs: "22px 20px", md: "30px 32px" },
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 0,
          }}
        >
          {eyebrow ? (
            <Box
              sx={{
                display: "flex",
                gap: 1.25,
                alignItems: "center",
                flexWrap: "wrap",
                fontSize: 13,
                fontWeight: 600,
                color: "heroMuted",
              }}
            >
              {eyebrow}
            </Box>
          ) : null}
          <Typography
            component={component}
            id={titleId}
            sx={{
              m: 0,
              color: "heroInk",
              fontSize: { xs: Math.round(titleSize * 0.75), md: titleSize },
              fontWeight: 900,
              fontStretch: fontStretch.display,
              textTransform: "uppercase",
              letterSpacing: "-.01em",
              lineHeight: 0.95,
            }}
          >
            {title}
          </Typography>
          {children ? <Box sx={{ color: "heroInk" }}>{children}</Box> : null}
        </Box>
        {aside ? (
          <Box
            sx={{
              justifySelf: { xs: "stretch", md: "end" },
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              alignItems: "stretch",
              minWidth: { md: 260 },
            }}
          >
            {aside}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export interface HeroPanelProps {
  children: ReactNode;
}

/** Translucent panel on a hero surface (design `--hero-panel` with a 2 px `--hero-line` border). */
export const HeroPanel = ({ children }: HeroPanelProps) => {
  const { mode } = useColorMode();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        p: "16px 20px",
        border: `2px solid ${heroLine[mode]}`,
        borderRadius: "16px",
        bgcolor: heroPanel[mode],
        backdropFilter: "blur(4px)",
        color: "heroInk",
      }}
    >
      {children}
    </Box>
  );
};

export default HeroBand;
